import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NavigationProvider, useNavigation } from './context/NavigationContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AuthFlow } from './pages/auth/AuthFlow';
import { ProfilePage } from './pages/ProfilePage';
import { MasterDataPage } from './pages/admin/MasterDataPage';
import { ComplaintListPage } from './pages/complaint/ComplaintListPage';
import { ComplaintDetailPage } from './pages/complaint/ComplaintDetailPage';
import { OfficerWorkspacePage } from './pages/workflow/OfficerWorkspacePage';
import { SupervisorWorkspacePage } from './pages/workflow/SupervisorWorkspacePage';
import { WorkOrderListPage } from './pages/workOrder/WorkOrderListPage';
import { WorkOrderDetailPage } from './pages/workOrder/WorkOrderDetailPage';
import { VendorManagementPage } from './pages/vendor/VendorManagementPage';
import { TechnicianWorkspacePage } from './pages/technician/TechnicianWorkspacePage';
import { AdminDashboardPage } from './pages/dashboard/AdminDashboardPage';
import { OfficerDashboardPage } from './pages/dashboard/OfficerDashboardPage';
import { SupervisorDashboardPage } from './pages/dashboard/SupervisorDashboardPage';
import { VendorDashboardPage } from './pages/dashboard/VendorDashboardPage';
import { ReportsPage } from './pages/dashboard/ReportsPage';
import { AdminConfigPage } from './pages/admin/AdminConfigPage';
import { Complaint } from './types/complaint';
import { WorkOrder } from './types/workOrder';
import { MainLayout } from './layouts/MainLayout';
import { AppTab } from './config/navigation';

const tabTitleMap: Record<AppTab, string> = {
  dashboard: 'Operations Dashboard',
  reports: 'Reports & Analytics',
  complaints: 'Complaint Register',
  workorders: 'Work Order Management',
  'my-workorders': 'My Work Orders',
  officer: 'Officer Triage Workspace',
  supervisor: 'Supervisor Operations',
  vendors: 'Vendor & Contractor Management',
  technician: 'Technician Work Execution',
  masters: 'System Master Configuration',
  'admin-config': 'System Administration & Configuration',
  profile: 'User Account Profile',
};

const DashboardContent: React.FC = () => {
  const { user } = useAuth();
  const { activeTab, setActiveTab } = useNavigation();
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null);

  useEffect(() => {
    if (activeTab !== 'complaints') setSelectedComplaint(null);
    if (activeTab !== 'workorders') setSelectedWorkOrder(null);
  }, [activeTab]);

  const handleSelectComplaint = (complaint: Complaint) => {
    setSelectedComplaint(complaint);
    setActiveTab('complaints');
  };

  const handleSelectWorkOrder = (wo: WorkOrder) => {
    setSelectedWorkOrder(wo);
    setActiveTab('workorders');
  };

  const getPageTitle = () => {
    if (activeTab === 'complaints' && selectedComplaint) return `Complaint ${selectedComplaint.complaint_id}`;
    if (activeTab === 'workorders' && selectedWorkOrder) return `Work Order ${selectedWorkOrder.work_order_id}`;
    return tabTitleMap[activeTab] || 'Dashboard';
  };

  const renderDashboard = () => {
    switch (user?.role) {
      case 'OFFICER': return <OfficerDashboardPage />;
      case 'SUPERVISOR': return <SupervisorDashboardPage />;
      case 'VENDOR': return <VendorDashboardPage />;
      case 'TECHNICIAN': return <TechnicianWorkspacePage />;
      case 'DEPARTMENT_USER': return <ComplaintListPage onSelectComplaint={handleSelectComplaint} />;
      default: return <AdminDashboardPage onSelectComplaint={handleSelectComplaint} />;
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="border-b border-slate-800 pb-4">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-100">
            {getPageTitle()}
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Logged in as <span className="text-sky-400 font-semibold">{user?.name}</span> ({user?.role})
          </p>
        </div>

        {activeTab === 'profile' && <ProfilePage />}
        {activeTab === 'masters' && <MasterDataPage />}
        {activeTab === 'vendors' && <VendorManagementPage />}
        {activeTab === 'technician' && <TechnicianWorkspacePage />}
        {activeTab === 'admin-config' && <AdminConfigPage />}
        {activeTab === 'officer' && <OfficerWorkspacePage onSelectComplaint={handleSelectComplaint} />}
        {activeTab === 'supervisor' && <SupervisorWorkspacePage onSelectComplaint={handleSelectComplaint} />}
        {activeTab === 'reports' && <ReportsPage />}
        {activeTab === 'complaints' && (
          selectedComplaint ? (
            <ComplaintDetailPage complaint={selectedComplaint} onBack={() => setSelectedComplaint(null)} />
          ) : (
            <ComplaintListPage onSelectComplaint={handleSelectComplaint} />
          )
        )}
        {activeTab === 'workorders' && (
          selectedWorkOrder ? (
            <WorkOrderDetailPage workOrder={selectedWorkOrder} onBack={() => setSelectedWorkOrder(null)} />
          ) : (
            <WorkOrderListPage onSelectWorkOrder={handleSelectWorkOrder} />
          )
        )}
        {activeTab === 'my-workorders' && (
          selectedWorkOrder ? (
            <WorkOrderDetailPage workOrder={selectedWorkOrder} onBack={() => setSelectedWorkOrder(null)} />
          ) : (
            <WorkOrderListPage onSelectWorkOrder={handleSelectWorkOrder} />
          )
        )}

        {activeTab === 'dashboard' && renderDashboard()}
      </div>
    </MainLayout>
  );
};

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <ProtectedRoute fallbackView={<AuthFlow />}>
        <NavigationProvider>
          <DashboardContent />
        </NavigationProvider>
      </ProtectedRoute>
    </AuthProvider>
  );
};

export default App;
